import type { ToneAudioBuffer } from "tone";
import { registerAudioBuffers, resetAudioBudgetForTests } from "@/lib/perf/audio-budget-tracker";
import { leadingSilenceSeconds } from "./sample-onset";

/**
 * Les échantillons décodés, une fois pour toute l'application.
 *
 * # Pourquoi un cache applicatif alors que le navigateur en a un
 *
 * Le cache HTTP évite le téléchargement, pas le **décodage**. Un
 * `Tone.Sampler` construit avec des URLs refait passer ses trente MP3 par
 * `decodeAudioData` à chaque instanciation, et ce travail-là se paie sur le
 * fil principal : ouvrir un morceau à deux mains, ou entrer en séance à
 * plusieurs, multipliait ce coût par le nombre de lanes pour un résultat
 * identique au bit près. Un `ToneAudioBuffer` est immuable et se partage sans
 * précaution entre samplers — c'est exactement ce qu'on veut mutualiser.
 *
 * # Mémoriser la promesse, pas le résultat
 *
 * Deux lanes qui démarrent ensemble demandent la même URL dans le même tour de
 * boucle. Un cache qui n'enregistre que le buffer fini laisserait les deux
 * partir en décodage avant que l'un ait terminé, et on n'aurait rien gagné.
 * C'est donc la promesse qui entre dans la table, dès le premier appel : le
 * second appelant s'y accroche.
 *
 * # Un échec ne s'installe pas
 *
 * En cas de rejet l'entrée est retirée. Sans ça une coupure réseau d'une
 * seconde condamnerait cet échantillon pour toute la durée de l'onglet, alors
 * qu'un simple rappel suffit à réessayer.
 *
 * # Et le rognage
 *
 * C'est aussi le seul endroit par où passent tous les échantillons, donc celui
 * où retirer une fois pour toutes le silence qui précède leur attaque — une
 * douzaine de millisecondes de retard pur sur chaque note, mesurées dans les
 * MP3 eux-mêmes. Voir `sample-onset.ts` et `trimLeadingSilence`.
 */

/**
 * Le strict nécessaire du module `tone`. Passé en paramètre plutôt qu'importé :
 * les synthés chargent Tone dynamiquement (le module touche `window` à
 * l'import), et ce cache n'a aucune raison de forcer un second chargement.
 */
export type SampleBufferLoader = {
  readonly ToneAudioBuffer: { fromUrl(url: string): Promise<ToneAudioBuffer> };
};

/**
 * Ce qu'un buffer doit savoir faire pour être rogné. `ToneAudioBuffer` le sait ;
 * l'interface est là pour que le rognage se lise et s'éprouve sans Tone.
 */
export interface TrimmableBuffer {
  readonly numberOfChannels: number;
  readonly sampleRate: number;
  getChannelData(channel: number): Float32Array;
  slice(start: number): TrimmableBuffer;
}

/**
 * Le même buffer, moins le silence qui précède son attaque.
 *
 * Fait ici, au sortir du décodage, parce que le sampler de Tone démarre
 * toujours à l'offset zéro : il n'y a rien à lui dire, le buffer commence au
 * coup de marteau. Le buffer d'origine n'est plus référencé ensuite et part au
 * ramasse-miettes ; ce que le cache retient est déjà rogné. La mesure et la
 * règle sont dans `sample-onset.ts`.
 */
export function trimLeadingSilence<T extends TrimmableBuffer>(buffer: T): T {
  const channels: Float32Array[] = [];

  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    channels.push(buffer.getChannelData(channel));
  }

  const silence = leadingSilenceSeconds(channels, buffer.sampleRate);

  if (silence <= 0) {
    return buffer;
  }

  return buffer.slice(silence) as T;
}

const decoding = new Map<string, Promise<ToneAudioBuffer>>();

/**
 * Le buffer décodé pour cette URL, partagé avec tous ceux qui la demandent —
 * et rogné de son silence d'amorce, voir `trimLeadingSilence`.
 */
export function loadSampleBuffer(
  Tone: SampleBufferLoader,
  url: string,
): Promise<ToneAudioBuffer> {
  const pending = decoding.get(url);

  if (pending !== undefined) {
    return pending;
  }

  const loading = Tone.ToneAudioBuffer.fromUrl(url)
    .then((decoded) => {
      const trimmed = trimLeadingSilence(decoded);

      // Pesé ici, une fois par URL : c'est le seul endroit par où entre un
      // buffer décodé, et il est partagé ensuite. C'est ce compteur que
      // `SalamanderPianoSynth.loadLayer` consulte avant une couche de plus.
      registerAudioBuffers([trimmed]);

      return trimmed;
    })
    .catch((error: unknown) => {
      decoding.delete(url);

      throw error;
    });

  decoding.set(url, loading);

  return loading;
}

/**
 * Décode un jeu d'échantillons nommés, tel que `Tone.Sampler` l'attend.
 *
 * Rend la même forme que l'option `urls` d'un sampler, mais peuplée de buffers
 * au lieu de chemins : c'est la substitution qui supprime le décodage.
 * `baseUrl` est concaténée ici, une fois, pour que la clé du cache soit l'URL
 * réellement chargée — deux origines différentes sont deux fichiers différents.
 */
export async function loadSampleBuffers(
  Tone: SampleBufferLoader,
  baseUrl: string,
  files: Record<string, string>,
): Promise<Record<string, ToneAudioBuffer>> {
  const names = Object.keys(files);
  const buffers = await Promise.all(
    names.map((name) => loadSampleBuffer(Tone, baseUrl + files[name])),
  );

  const map: Record<string, ToneAudioBuffer> = {};

  names.forEach((name, index) => {
    map[name] = buffers[index];
  });

  return map;
}

/** Combien d'URLs distinctes le cache tient. Pour les tests et le débogage. */
export function sampleBufferCacheSize(): number {
  return decoding.size;
}

/**
 * Cette URL est-elle déjà en mémoire, ou en train d'y entrer ? Sert à estimer
 * ce qu'une couche coûterait *en plus* : ce qui est déjà là ne pèse rien.
 */
export function isSampleBufferCached(url: string): boolean {
  return decoding.has(url);
}

/** @internal Remet le cache — et le compteur de mémoire — à zéro entre deux tests. */
export function clearSampleBufferCache(): void {
  decoding.clear();
  resetAudioBudgetForTests();
}
